/**
 * @linkhr/hr-domain/bank-file-errors — Zod boundary for BANK_FILE_VALIDATION_FAILED.
 *
 * The backend's `POST /payroll/runs/{runId}/bank-file` returns HTTP 400 with
 * errorCode "BANK_FILE_VALIDATION_FAILED" and a non-standard RFC 7807 extension
 * property `rowErrors[]`. The generated client types don't surface this extension.
 *
 * This module provides:
 *   - `RowError` — per-row IBAN validation error
 *   - `BankFileValidationError` — the full 400 body shape
 *   - `parseBankFileError(raw)` — null-returning parser (never throws)
 *   - `isValidIban(raw)` — client-side IBAN pre-validation
 *
 * Decision D-19 (CONTEXT.md): per-row IBAN validation UI surfaces these errors
 * inline in the run detail panel. Hook layer calls `parseBankFileError(error.body)`
 * and renders the rowErrors table if non-null.
 *
 * Per project conventions Testing Philosophy this is MUST-test tier (RFC 7807 parsing at
 * a security/correctness boundary).
 */

import { z } from "zod";

/**
 * Total IBAN length per country, from the SWIFT IBAN registry. A country
 * missing from this table has not adopted IBAN, so a string carrying its
 * prefix is not an IBAN however well-formed it looks — an unlisted prefix is
 * rejected rather than waved through on the checksum alone.
 */
const IBAN_LENGTH_BY_COUNTRY: Readonly<Record<string, number>> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BI: 27,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DJ: 27,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FK: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HN: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MN: 20,
  MR: 27,
  MT: 31,
  MU: 30,
  NI: 28,
  NL: 18,
  NO: 15,
  OM: 23,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  RU: 33,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  SO: 23,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
  YE: 30,
};

/** ISO 13616 shape: country, two check digits, then the national part. */
const IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]+$/;

/**
 * IBAN validation: ISO 13616 shape, the country's registered length, PLUS the
 * ISO 7064 mod-97 checksum. The shape alone accepts checksum-invalid strings;
 * the backend enforces the checksum (rejects with `EMPLOYEE_IBAN_INVALID`), so
 * validating it client-side gives immediate feedback instead of a 400. Input is
 * normalized (whitespace stripped, upper-cased) before checking; empty is NOT
 * valid here (callers gate optional/empty separately).
 *
 * MUST-test tier: IBAN checksum is load-bearing correctness math.
 */
export function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s+/g, "").toUpperCase();
  if (!IBAN_REGEX.test(iban)) return false;
  if (iban.length !== IBAN_LENGTH_BY_COUNTRY[iban.slice(0, 2)]) return false;
  // ISO 13616: move the first 4 chars to the end, map letters A=10..Z=35, then
  // the resulting number mod 97 must equal 1. Fold digit-by-digit so we never
  // build a 30+ digit BigInt: a mapped letter contributes two decimal digits.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const value = ch >= "0" && ch <= "9" ? ch.charCodeAt(0) - 48 : ch.charCodeAt(0) - 55; // A(65)->10
    remainder = value > 9 ? (remainder * 100 + value) % 97 : (remainder * 10 + value) % 97;
  }
  return remainder === 1;
}

/**
 * A single per-row error from the bank-file validation.
 * reasonCode matches the backend's BankFileValidationException row-error enum.
 */
export const RowError = z.object({
  rowNumber: z.number().int().positive(),
  employeeCode: z.string(),
  reasonCode: z.enum(["IBAN_MISSING", "IBAN_INVALID"]),
  // Optional human-friendly hints from backend
  employeeName: z.string().optional(),
  employeeId: z.string().optional(),
});

export type RowError = z.infer<typeof RowError>;

/**
 * The full RFC 7807 body for a BANK_FILE_VALIDATION_FAILED response.
 * Uses `z.literal` to ensure this parser only accepts the specific errorCode.
 */
export const BankFileValidationError = z.object({
  errorCode: z.literal("BANK_FILE_VALIDATION_FAILED"),
  rowErrors: z.array(RowError).default([]),
  title: z.string().optional(),
  correlationId: z.string().optional(),
});

export type BankFileValidationError = z.infer<typeof BankFileValidationError>;

/**
 * Parses a raw error body as a BANK_FILE_VALIDATION_FAILED response.
 * Returns null if `raw` is not this error type — allows the caller to
 * fall through to standard error handling.
 *
 * Usage at the hook layer:
 *
 *   const bankFileError = parseBankFileError(error.body);
 *   if (bankFileError) {
 *     // render per-row IBAN error table
 *   } else {
 *     // standard error banner
 *   }
 */
export function parseBankFileError(raw: unknown): BankFileValidationError | null {
  const result = BankFileValidationError.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * One payroll line the bank file cannot pay.
 *
 * The default export policy is EXCLUDE: the file generates for every payable
 * line and the unpayable ones come back as this manifest. The same array
 * arrives from three places — the manifest GET, the strict-mode 400, and the
 * refusal when every line is excluded — so everything downstream reads this
 * one shape.
 */
export const BankFileExclusion = z.object({
  rowNumber: z.number().int().positive(),
  employeeId: z.string(),
  employeeCode: z.string().nullish(),
  // The person, resolved server-side in the reader's language with the same
  // cross-script fallback the enrollment and readiness rows use. Without it
  // this worklist was the one surface naming people by employee code. Absent
  // on rows served before it shipped, so readers fall back to the code.
  employeeName: z.string().nullish(),
  // NON_BANK_METHOD: paid on another rail (CliQ, cash, cheque, money transfer),
  // so left out on purpose. CORRECTION_NO_PAYMENT: a correction line that pays
  // nothing (no difference, or money asked back instead).
  // A reason this client does not know yet reads as OTHER — one unfamiliar row
  // must not fail the whole list and blank everyone else on it.
  reason: z
    .enum([
      "ZERO_SALARY",
      "NET_NON_POSITIVE",
      "MISSING_BANK",
      "IBAN_INVALID",
      "SPLIT_INVALID",
      "NON_BANK_METHOD",
      "CORRECTION_NO_PAYMENT",
      "OTHER",
    ])
    .catch("OTHER"),
  // On SPLIT_INVALID: the account that failed the split checks, as bank name
  // plus the last four of its number. Null on every other reason.
  accountLabel: z.string().nullish(),
});

export type BankFileExclusion = z.infer<typeof BankFileExclusion>;

/**
 * Read the exclusions manifest off anything that can carry it: the manifest
 * endpoint's bare array, or a problem body carrying it under `exclusions`.
 *
 * The older per-row IBAN shape (`rowErrors[]` with IBAN_MISSING/IBAN_INVALID)
 * is folded into the same reasons so strict-mode tenants — whose refusal still
 * speaks the old shape — render through the same worklist instead of a second
 * table that has to be kept in step.
 *
 * Returns null when `raw` carries no manifest at all, so callers fall through
 * to ordinary error handling.
 */
export function parseBankFileExclusions(raw: unknown): BankFileExclusion[] | null {
  const bare = z.array(BankFileExclusion).safeParse(raw);
  if (bare.success) return bare.data;

  const wrapped = z.object({ exclusions: z.array(BankFileExclusion) }).safeParse(raw);
  if (wrapped.success) return wrapped.data.exclusions;

  const legacy = parseBankFileError(raw);
  if (legacy === null) return null;
  return legacy.rowErrors.map((r) => ({
    rowNumber: r.rowNumber,
    employeeId: r.employeeId ?? "",
    employeeCode: r.employeeCode,
    // The legacy shape already carried a name; dropping it here made a
    // strict-mode tenant's worklist show codes where everyone else saw people.
    ...(r.employeeName === undefined ? {} : { employeeName: r.employeeName }),
    reason: r.reasonCode === "IBAN_MISSING" ? ("MISSING_BANK" as const) : ("IBAN_INVALID" as const),
  }));
}

/**
 * Where an exclusion is fixed. A bank problem is fixed in the account section,
 * a pay problem in the salary — so the row links to the part that actually
 * needs the edit. Null when there is nothing to fix: the person is paid another
 * way, or the reason is one this client does not know.
 *
 * Also takes the reasons a payment's destination is invalid. A missing CliQ
 * identifier is fixed beside the bank accounts, where the CliQ destination is
 * edited.
 */
export function exclusionFixTarget(
  reason: BankFileExclusion["reason"] | "CLIQ_IDENTIFIER_MISSING",
): "bank" | "compensation" | null {
  if (
    reason === "MISSING_BANK" ||
    reason === "IBAN_INVALID" ||
    reason === "SPLIT_INVALID" ||
    reason === "CLIQ_IDENTIFIER_MISSING"
  )
    return "bank";
  if (reason === "ZERO_SALARY" || reason === "NET_NON_POSITIVE") return "compensation";
  return null;
}

/** Where one payroll run exception is fixed (see `exceptionFixTarget`). */
export type ExceptionFixTarget =
  | {
      kind: "employee";
      employeeId: string;
      tab: "compensation" | "job" | "bank" | "personal" | "lifecycle" | "overview";
      hash?: "one-time-payments";
    }
  | { kind: "overtimes" }
  | { kind: "tax" }
  | { kind: "attendance"; employeeId?: string; month?: string }
  | { kind: "payroll_runs" }
  | { kind: "run"; runId: string };

const EXCEPTION_EMPLOYEE_LINK = /^\/employees\/([^/?#]+)$/;
const EXCEPTION_RUN_LINK = /^\/payroll\/runs\/([^/?#]+)$/;

/**
 * Where a payroll run exception is fixed, from the server's link and the item's
 * code. The server names the profile section; this client knows the page that
 * holds it (pay, one-time payments and social insurance enrollment share the
 * salary page, under Job & Comp). Two
 * destinations are corrected here: the tax-bracket gap is fixed in tax
 * settings, where an older server points at payroll settings, and an unpaid
 * extra salary is added as a one-time payment. Null when there is nothing to
 * open: a path this client doesn't know, the run being viewed, or an
 * enrollment where the company has no enrollment section to edit.
 */
export function exceptionFixTarget(
  code: string,
  link: { path: string; section?: string | null },
  opts: {
    currentRunId?: string;
    enrollmentEditable?: boolean;
    /** Who the item is about and its first day, which lead to that person's attendance month. */
    employeeId?: string | null | undefined;
    coverageFrom?: string | null | undefined;
  } = {},
): ExceptionFixTarget | null {
  if (
    link.path === "/settings/tax" ||
    (code === "MISSING_TAX_BRACKET" && link.path === "/settings/payroll")
  )
    return { kind: "tax" };
  const employeeId = EXCEPTION_EMPLOYEE_LINK.exec(link.path)?.[1];
  if (employeeId !== undefined) {
    const on = (
      tab: "compensation" | "job" | "bank" | "personal" | "lifecycle" | "overview",
      hash?: "one-time-payments",
    ): ExceptionFixTarget => ({
      kind: "employee",
      employeeId,
      tab,
      ...(hash !== undefined ? { hash } : {}),
    });
    switch (link.section) {
      case "COMPENSATION":
        return code === "EXTRA_SALARY_UNPAID"
          ? on("compensation", "one-time-payments")
          : on("compensation");
      case "ENROLLMENT":
        return opts.enrollmentEditable === false ? null : on("compensation");
      case "BANK":
        return on("bank");
      case "IDENTITY":
        return on("personal");
      case "CONTRACT":
        return on("job");
      case "LIFECYCLE":
        return on("lifecycle");
      case "OVERTIME":
        return { kind: "overtimes" };
      default:
        return on("overview");
    }
  }
  if (link.path === "/attendance") {
    if (opts.employeeId == null) return { kind: "attendance" };
    return {
      kind: "attendance",
      employeeId: opts.employeeId,
      ...(opts.coverageFrom != null ? { month: opts.coverageFrom.slice(0, 7) } : {}),
    };
  }
  if (link.path === "/payroll/runs") return { kind: "payroll_runs" };
  const runId = EXCEPTION_RUN_LINK.exec(link.path)?.[1];
  if (runId !== undefined) return runId === opts.currentRunId ? null : { kind: "run", runId };
  return null;
}

/**
 * The manifest split the way the pay step reads it. It lists every line the
 * file leaves out, on purpose: `fix` is the people the file should pay and
 * can't yet; `nothingToPay` pay nothing this month; `paidElsewhere` are paid on
 * another rail. A reason this client doesn't know lands in `fix`, so nothing
 * is ever hidden.
 */
export function groupBankFileExclusions(rows: readonly BankFileExclusion[]): {
  fix: BankFileExclusion[];
  nothingToPay: BankFileExclusion[];
  paidElsewhere: BankFileExclusion[];
} {
  const groups = {
    fix: [] as BankFileExclusion[],
    nothingToPay: [] as BankFileExclusion[],
    paidElsewhere: [] as BankFileExclusion[],
  };
  for (const row of rows) {
    const r = row.reason;
    if (r === "ZERO_SALARY" || r === "NET_NON_POSITIVE" || r === "CORRECTION_NO_PAYMENT")
      groups.nothingToPay.push(row);
    else if (r === "NON_BANK_METHOD") groups.paidElsewhere.push(row);
    else groups.fix.push(row);
  }
  return groups;
}
